import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, ArrowRight, ShieldCheck, Zap, BarChart3, Wand2 } from "lucide-react";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";
import { api, imageUrl } from "@/lib/api";

const HERO_IMG =
  "https://images.unsplash.com/photo-1580489944761-15a19d654956?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwxfHxiZWF1dGlmdWwlMjBjb25maWRlbnQlMjBzbWlsZXxlbnwwfHx8fDE3ODE0NzY4Nzl8MA&ixlib=rb-4.1.0&q=85";
const CLINIC_IMG =
  "https://images.unsplash.com/photo-1629909613654-28e377c37b09?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBkZW50YWwlMjBjbGluaWMlMjBpbnRlcmlvcnxlbnwwfHx8fDE3ODE0NzY4Nzh8MA&ixlib=rb-4.1.0&q=85";
const PATIENT_IMG =
  "https://images.unsplash.com/photo-1494790108377-be9c29b29330?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwyfHxiZWF1dGlmdWwlMjBjb25maWRlbnQlMjBzbWlsZXxlbnwwfHx8fDE3ODE0NzY4Nzl8MA&ixlib=rb-4.1.0&q=85";

export default function Landing() {
  const [demo, setDemo] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetchDemo = async (attempt = 0) => {
      try {
        const { data } = await api.get("/demo/preview");
        if (!cancelled) setDemo(data);
      } catch (e) {
        // Demo generation runs in background on cold start; retry a few times
        if (!cancelled && attempt < 12) {
          setTimeout(() => fetchDemo(attempt + 1), 5000);
        }
      }
    };
    fetchDemo();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="min-h-screen bg-bone-100 text-forest-700">
      {/* Header */}
      <header className="border-b border-bone-200 bg-bone-100/80 glass sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
          <Link to="/" data-testid="landing-brand" className="flex items-center gap-2">
            <Sparkles size={22} strokeWidth={1.5} className="text-forest-600" />
            <span className="font-heading text-2xl">Toothpix</span>
            <span className="overline ml-2 hidden sm:inline">AI Smile Preview</span>
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-sm">
            <a href="#how-it-works" className="text-forest-400 hover:text-forest-700 transition-colors">How it works</a>
            <a href="#why" className="text-forest-400 hover:text-forest-700 transition-colors">For Clinics</a>
            <a href="#pricing" className="text-forest-400 hover:text-forest-700 transition-colors">Pricing</a>
            <a href="#demo" className="text-forest-400 hover:text-forest-700 transition-colors">Live Demo</a>
          </nav>
          <div className="flex items-center gap-2">
            <Link to="/login" data-testid="login-link" className="btn-secondary !py-2 !px-5 hidden sm:inline-flex">
              Sign in
            </Link>
            <Link to="/signup" data-testid="signup-link" className="btn-primary !py-2 !px-5">
              Start trial
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-20 md:pt-32 pb-16 md:pb-24">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="md:col-span-7"
          >
            <div className="overline mb-6">Lead generation for orthodontists</div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.05] text-forest-700">
              Turn website visitors
              <br />
              into <em className="not-italic text-forest-600">booked consults</em>.
              <br />
              And never miss a follow-up.
            </h1>
            <p className="mt-8 max-w-xl text-lg text-forest-400 leading-relaxed">
              Toothpix gives your clinic a branded AI smile-preview page that
              turns a casual selfie into a photorealistic "after" and a
              high-intent lead. Then it follows up automatically until they book.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-3 items-start sm:items-center">
              <Link to="/signup" data-testid="hero-cta" className="btn-primary">
                Claim my clinic page <ArrowRight size={16} strokeWidth={1.5} />
              </Link>
              <Link to="/preview/bright-smile" data-testid="hero-demo-cta" className="btn-secondary">
                Try the patient demo
              </Link>
            </div>
            <div className="mt-10 flex flex-wrap items-center gap-8 text-xs text-forest-400">
              <Stat label="White-labelled" value="Your branding, not ours" />
              <Stat label="Fast" value="Preview in about 30 seconds" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="md:col-span-5"
          >
            <div className="relative">
              <div className="absolute -top-6 -left-6 w-32 h-32 bg-forest-100/50 -z-10" />
              <img src={HERO_IMG} alt="Confident smile" className="w-full aspect-[4/5] object-cover" />
              <div className="absolute -bottom-6 -right-6 bg-white border border-bone-200 p-6 shadow-md w-56">
                <div className="overline">Result delivered</div>
                <div className="font-heading text-2xl mt-2">~30 seconds</div>
                <div className="text-xs text-forest-400 mt-1">Typical AI render</div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Demo / Before-After */}
      <section id="demo" className="bg-white border-y border-bone-200 py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
            <div className="md:col-span-5">
              <div className="overline mb-4">The Toothpix Difference</div>
              <h2 className="text-4xl md:text-5xl leading-tight">
                Photoreal. Not gimmicky.
              </h2>
              <p className="mt-6 text-forest-400 leading-relaxed">
                We don't paste a stock smile. Our clinical model preserves the
                patient's lip line, jaw, lighting and skin tone, and only
                corrects the alignment and color of the teeth. Patients feel
                like they're seeing themselves, not a filter.
              </p>
              <div className="mt-8 space-y-4 text-sm">
                {[
                  "Preserves identity, expression, and lighting",
                  "Subtle whitening, natural and never bleached",
                  "Watermarked for clinical credibility",
                ].map((t) => (
                  <div key={t} className="flex items-start gap-3">
                    <span className="text-forest-600">·</span>
                    <span className="text-forest-700">{t}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="md:col-span-7">
              {demo ? (
                <BeforeAfterSlider
                  beforeSrc={imageUrl(demo.before_url)}
                  afterSrc={imageUrl(demo.after_url)}
                  aspect="aspect-square"
                />
              ) : (
                <div className="aspect-square w-full bg-bone-200/40 border border-bone-200 flex items-center justify-center">
                  <div className="text-center">
                    <div className="overline mb-3">Generating live demo…</div>
                    <div className="text-sm text-forest-400 max-w-xs">
                      Our AI is rendering a real clear-aligner before/after for the demo. Hold tight.
                    </div>
                  </div>
                </div>
              )}
              <p className="text-xs text-forest-400 mt-3 italic">
                Rendered by Toothpix. Drag the handle to compare a real AI-generated before/after.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how-it-works" className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
        <div className="overline mb-4">How it works</div>
        <h2 className="text-4xl md:text-5xl max-w-3xl leading-tight">
          One link. Three taps. A new lead in your inbox.
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 mt-16">
          {[
            {
              n: "01",
              t: "Share your branded link",
              d: "Drop your /preview/your-clinic link on Instagram, Google Maps, or send via SMS.",
              icon: Zap,
            },
            {
              n: "02",
              t: "Patient uploads a selfie",
              d: "They see your clinic name & colors, fill a 3-field lead form, then upload a smiling photo.",
              icon: Wand2,
            },
            {
              n: "03",
              t: "AI delivers a real preview",
              d: "In ~30s they see themselves with straight teeth. You receive the lead instantly.",
              icon: BarChart3,
            },
          ].map((s) => (
            <div key={s.n} className="bg-white border border-bone-200 p-8 md:p-10">
              <div className="overline">{s.n}</div>
              <s.icon size={28} strokeWidth={1.2} className="text-forest-600 mt-6" />
              <div className="font-heading text-2xl mt-6">{s.t}</div>
              <p className="text-sm text-forest-400 mt-3 leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* The product: three parts */}
      <section className="bg-white border-y border-bone-200 py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <div className="overline mb-4">More than a preview</div>
            <h2 className="text-4xl md:text-5xl leading-tight">
              The preview is the hook. The product is booked consults.
            </h2>
            <p className="mt-6 text-forest-400 leading-relaxed max-w-2xl">
              Toothpix is a lead-generation engine with three parts working together,
              so a curious visitor becomes a patient in your chair.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 mt-16">
            {[
              {
                icon: Wand2,
                t: "A branded preview page that captures leads",
                d: "Your clinic's name, colors and logo on a page that turns a selfie into a photoreal 'after' and collects the visitor's name, email and phone.",
              },
              {
                icon: BarChart3,
                t: "A lead dashboard with New / Contacted / Booked",
                d: "Every preview lands as a lead you can track through the pipeline. See who's new, who you've reached, and who booked.",
              },
              {
                icon: Zap,
                t: "An automatic 3-email follow-up that self-cancels",
                d: "If a lead doesn't book, Toothpix follows up over 7 days. The moment you mark them Booked, the remaining emails cancel themselves.",
              },
            ].map((s) => (
              <div key={s.t} className="border border-bone-200 p-8 md:p-10">
                <s.icon size={28} strokeWidth={1.2} className="text-forest-600" />
                <div className="font-heading text-2xl mt-6 leading-snug">{s.t}</div>
                <p className="text-sm text-forest-400 mt-3 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why */}
      <section id="why" className="bg-forest-600 text-white py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-5">
            <img src={CLINIC_IMG} alt="Modern clinic" className="w-full aspect-[4/3] object-cover" />
          </div>
          <div className="md:col-span-7">
            <div className="overline !text-bone-200/70 mb-4">For Clinics</div>
            <h2 className="text-4xl md:text-5xl leading-tight">
              The cheapest seat in your waiting room.
            </h2>
            <p className="mt-6 text-bone-100/80 max-w-xl leading-relaxed">
              One converted case pays for Toothpix many times over. Give every
              website visitor a reason to raise their hand, and a reason to book.
            </p>
            <div className="grid grid-cols-2 gap-8 mt-10">
              <Metric label="Setup time" value="Minutes" />
              <Metric label="Branding" value="White-labelled" />
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
        <div className="text-center max-w-2xl mx-auto">
          <div className="overline mb-4">Pricing</div>
          <h2 className="text-4xl md:text-5xl leading-tight">
            One plan. Built for serious clinics.
          </h2>
          <p className="mt-4 text-forest-400">
            Start free for 14 days. No card. Cancel any time.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 mt-16 max-w-3xl mx-auto">
          {[
            { name: "Clinic", price: "$499", per: "/ month", desc: "3 locations · 250 previews/mo · lead CRM · analytics · website embed.", cta: "Start free trial", featured: true },
            { name: "Group", price: "Custom", per: "", desc: "Unlimited locations, SSO, white-label portal.", cta: "Talk to sales" },
          ].map((p) => (
            <div
              key={p.name}
              className={`p-8 md:p-10 border ${p.featured ? "bg-forest-600 text-white border-forest-600" : "bg-white border-bone-200"}`}
            >
              <div className={`overline ${p.featured ? "!text-bone-200/70" : ""}`}>{p.name}</div>
              <div className="font-heading text-5xl mt-4 flex items-baseline gap-1">
                {p.price}
                <span className={`text-sm font-body ${p.featured ? "text-bone-100/70" : "text-forest-400"}`}>{p.per}</span>
              </div>
              <p className={`mt-4 text-sm leading-relaxed ${p.featured ? "text-bone-100/80" : "text-forest-400"}`}>{p.desc}</p>
              <Link
                to={p.name === "Group" ? "mailto:hello@toothpix.ai" : "/signup"}
                data-testid={`pricing-${p.name.toLowerCase()}-cta`}
                className={`mt-8 inline-flex items-center gap-2 px-6 py-3 border transition-colors ${
                  p.featured
                    ? "bg-white text-forest-600 border-white hover:bg-bone-100"
                    : "border-forest-600 text-forest-600 hover:bg-forest-600 hover:text-white"
                }`}
              >
                {p.cta} <ArrowRight size={14} strokeWidth={1.5} />
              </Link>
            </div>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-white border-t border-bone-200 py-24 md:py-32">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <ShieldCheck size={32} strokeWidth={1.2} className="text-forest-600 mx-auto" />
          <h2 className="text-4xl md:text-5xl mt-6 leading-tight">
            Built for clinics. Trusted by patients.
          </h2>
          <p className="mt-4 text-forest-400 max-w-xl mx-auto">
            Photos are processed once, stored privately, and never used to
            train external models.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/signup" data-testid="final-cta-signup" className="btn-primary">
              Start 14-day trial <ArrowRight size={16} />
            </Link>
            <Link to="/preview/bright-smile" data-testid="final-cta-demo" className="btn-secondary">
              See patient experience
            </Link>
          </div>
          <p className="mt-8 text-xs text-forest-400">
            Questions? Email us at <a href="mailto:hello@toothpix.ai" className="text-forest-600 underline underline-offset-4" data-testid="contact-email">hello@toothpix.ai</a>
          </p>
        </div>
      </section>

      <footer className="border-t border-bone-200 py-10 px-6 md:px-12">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between gap-4 text-xs text-forest-400">
          <div>© {new Date().getFullYear()} Toothpix. All rights reserved. · A Zahar Lab product</div>
          <div className="flex gap-6">
            <a href="mailto:hello@toothpix.ai" className="hover:text-forest-700" data-testid="footer-email">hello@toothpix.ai</a>
            <Link to="/privacy" className="hover:text-forest-700" data-testid="footer-privacy">Privacy</Link>
            <Link to="/terms" className="hover:text-forest-700" data-testid="footer-terms">Terms</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="font-heading text-2xl text-forest-700">{value}</div>
      <div className="overline mt-1">{label}</div>
    </div>
  );
}
function Metric({ label, value }) {
  return (
    <div>
      <div className="overline !text-bone-200/70">{label}</div>
      <div className="font-heading text-3xl mt-1">{value}</div>
    </div>
  );
}
